import { Autocomplete } from '@common/ui'
import { com } from '@eidu/web'
import EntityReference from '../data/EntityReference'
import EqualityHashMap from '../../util/EqualityHashMap'
import EntityWithLabelAndRelated from '../../domain/entity/EntityWithLabelAndRelated'
import EntityTypeId = com.eidu.entity.type.EntityTypeId
import EntityType = com.eidu.entity.type.EntityType
import EntityId = com.eidu.entity.EntityId
import entityIdFromString = com.eidu.entity.entityIdFromString

export type EntityAutocompleteProps = {
  entitiesById: EqualityHashMap<EntityId, EntityWithLabelAndRelated>
  entityTypesById: ReadonlyMap<EntityTypeId, EntityType | null>
  value: EntityId | undefined
  onChange: (id: EntityId | undefined) => void
  onSearchChange: (query: string) => void
  loadMore?: () => void
  hasMore?: boolean
  loading?: boolean
  label: string
  required?: boolean
  error?: boolean
  disabled?: boolean
  noOptionsText?: string
  dropdownWithinPortal?: boolean
}

const EntityAutocomplete = ({
  entitiesById,
  entityTypesById,
  value,
  onChange,
  onSearchChange,
  loadMore,
  hasMore = false,
  loading = false,
  label,
  required,
  error,
  disabled,
  noOptionsText = 'No entities',
  dropdownWithinPortal = true,
}: EntityAutocompleteProps) => {
  const optionsBase = [...entitiesById.keys()].map((id) => id.toString())
  const optionIds = value ? [value.toString(), ...optionsBase.filter((id) => id !== value.toString())] : optionsBase
  const data = optionIds.map((id) => ({
    value: id,
    label: entitiesById.get(entityIdFromString(id))?.primaryLabelText ?? id,
  }))

  const selectedLabel = value ? entitiesById.get(value)?.primaryLabelText ?? value.toString() : ''
  const isInternalLabelSync = (query: string) => query !== '' && query === selectedLabel

  return (
    <Autocomplete
      disabled={disabled}
      data={data}
      serverFiltered
      loading={loading}
      loadMore={loadMore}
      hasMore={hasMore}
      required={required}
      label={label}
      error={error}
      comboboxProps={{ withinPortal: dropdownWithinPortal }}
      nothingFoundMessage={noOptionsText}
      value={value?.toString() ?? null}
      onChange={(newValue) => {
        if (!newValue) {
          onChange(undefined)
        } else {
          const parsed = entityIdFromString(newValue)
          onChange(parsed ?? undefined)
        }
      }}
      onSearchChange={(query) => {
        if (!isInternalLabelSync(query)) onSearchChange(query)
      }}
      renderOption={({ option }) => {
        const entity = entitiesById.get(entityIdFromString(option.value))
        const entityType = entity && entityTypesById.get(entity.entity.typeId)
        return entity ? (
          <EntityReference
            id={entityIdFromString(option.value)}
            typeId={entity.entity.typeId}
            label={entity.primaryLabelText}
            secondaryLabel={entity.secondaryLabelText}
            labelAsLink={false}
            typeName={entityType?.name}
          />
        ) : null
      }}
    />
  )
}

export default EntityAutocomplete
